<template>

    <div class="flex items-center justify-between mb-8">
        <Head :title="$t('projects.newProject')" />
        <page-title :title="$t('projects.newProject')"></page-title>
    </div>

    <jet-form-section @submitted="createProject">

        <template #form>

            <!-- Card General Project Information -->
            <form-card :title="$t('projects.project')" :description="$t('projects.projectDescription')">
                <div class="col-span-6">
                    <label for="name" class="block text-sm font-medium text-gray-700">{{ $t("projects.name") }} <RequiredMark /></label>
                    <input type="text" name="name" id="name" v-model="form.name"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                           autofocus />
                    <jet-input-error :message="form.errors.name" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="number" class="block text-sm font-medium text-gray-700">{{ $t("projects.number") }} <span class="text-gray-400">({{ $t("forms.optional") }})</span></label>
                    <input type="text" name="number" id="number" v-model="form.number"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors.number" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="street" class="block text-sm font-medium text-gray-700">{{
                            $t("projects.street")
                        }} <RequiredMark /></label>
                    <input type="text" name="street" id="street" v-model="form.location.street"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['location.street']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-1">
                    <label for="zip" class="block text-sm font-medium text-gray-700">{{ $t("projects.zip") }} <RequiredMark /></label>
                    <input type="text" name="zip" id="zip" v-model="form.location.zip"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.zip']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{ $t("projects.city") }} <RequiredMark /></label>
                    <input type="text" name="city" id="city" v-model="form.location.city"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300">
                    <jet-input-error :message="form.errors['location.city']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-2">
                    <label for="city" class="block text-sm font-medium text-gray-700">{{
                            $t("projects.country")
                        }} <RequiredMark /></label>
                    <Dropdown v-model="form.location.country"
                              :options="countries"
                              optionValue="code"
                              optionLabel="name"
                              :placeholder="$t('forms.selectCountry')"
                              class="mt-1 w-full" />
                    <jet-input-error :message="form.errors['location.country']" class="mt-2" />
                </div>           

                <hr class="w-full" />

                <div class="col-span-6">

                    <label class="block text-sm font-medium text-gray-700">
                        {{ $t("projects.image") }} <RequiredMark />
                    </label>
                    <div class="mt-1 flex items-center space-x-5">

                        <!-- New Project Image Preview -->
                        <div class="mt-2" v-show="imagePreview">
                            <span class="block w-48 h-20 bg-cover bg-no-repeat bg-center"
                                  :style="'background-image: url(\'' + imagePreview + '\');'"></span>
                        </div>

                        <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto"
                                              v-if="!imagePreview">{{ $t("projects.chooseImage") }}
                        </jet-secondary-button>
                        <jet-secondary-button class="mt-2 mr-2" type="button" @click.prevent="clearPhotoFileInput"
                                              v-if="imagePreview">{{ $t("projects.removeImage") }}
                        </jet-secondary-button>

                        <!-- Project Image File Input -->
                        <input type="file" class="hidden" ref="image" accept="image/png, image/jpeg, image/heic"
                               @change="updateImagePreview" />
                    </div>
                    <p class="my-3 text-sm text-gray-400">{{ $t("projects.fileInfo", { size: maxSizeInMb }) }}</p>
                    <jet-input-error :message="form.errors.image" class="mt-2" />
                </div>
            </form-card>

            <!-- Card Project Settings -->
            <form-card :title="$t('projects.settings')" :description="$t('projects.settingsDescription')">
                <div class="col-span-3">
                    <label for="base_fee" class="block text-sm font-medium text-gray-700">{{ $t("projects.baseFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="base_fee" id="base_fee" v-model="form.base_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div>       
                    <jet-input-error :message="form.errors['base_fee']" class="mt-2" />
                </div>

                <div class="col-span-3">
                    <label for="consultancy_fee " class="block text-sm font-medium text-gray-700">{{ $t("projects.consultancyFee") }} <RequiredMark /></label>
                    <div class="flex">
                        <input type="number" step="0.01" name="consultancy_fee" id="consultancy_fee" v-model="form.consultancy_fee" class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                        <span class="inline-flex items-center px-3 border border-l-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1">%</span>
                    </div> 
                    <jet-input-error :message="form.errors['consultancy_fee']" class="mt-2" />
                </div> 

                <!-- Auftrag Luca, 2026-08-20, Entscheide 1 und 5: die Wahl
                     steht direkt unter Gebuehr und Honorar, weil sie genau
                     deren Rechenart bestimmt. Vorbelegt aus der Agentur, mit
                     sichtbarem Hinweis, dass sie endgueltig ist — bewusst
                     OHNE Bestaetigungs-Checkbox. -->
                <div class="col-span-6">
                    <label class="block text-sm font-medium text-gray-700">{{ $t("projects.feeCalculationModeLabel") }} <RequiredMark /></label>

                    <div class="mt-2 space-y-3">
                        <label
                            v-for="option in feeCalculation.options"
                            :key="option"
                            class="flex items-start gap-3 text-sm text-gray-700 cursor-pointer select-none"
                            :for="'fee-calculation-mode-' + option"
                        >
                            <input
                                :id="'fee-calculation-mode-' + option"
                                v-model="form.fee_calculation_mode"
                                type="radio"
                                name="fee_calculation_mode"
                                class="mt-1"
                                :value="option"
                            />
                            <span>
                                <span class="block font-medium">
                                    {{ $t("projects.feeCalculationModes." + option) }}
                                    <!-- Entscheid Peter, 31.08.2026: Die offertenbasierte
                                         Rechnung ist der Normalfall; budgetbasiert ist die
                                         Ausnahme. Weil die Wahl nach dem Anlegen feststeht,
                                         wird der Standard hier benannt statt nur beschrieben. -->
                                    <span
                                        v-if="option === 'offer_based'"
                                        class="ml-2 align-middle inline-block px-2 py-0.5 rounded border border-gray-300 bg-white text-[11px] font-medium text-gray-500"
                                    >{{ $t("projects.feeCalculationModeDefaultBadge") }}</span>
                                </span>
                                <!-- Der einleitende Satz «Nur in Ausnahmefällen.» steht
                                     etwas dunkler als der übrige Hinweis, damit beide
                                     Optionen ihre Empfehlung gleich laut tragen. -->
                                <span class="block text-xs text-gray-500">
                                    <span
                                        v-if="option === 'budget_based'"
                                        class="text-gray-600 font-medium"
                                    >{{ $t("projects.feeCalculationModeExceptionHint") }}</span>
                                    {{ $t("projects.feeCalculationModeHints." + option) }}
                                </span>
                            </span>
                        </label>
                    </div>

                    <p class="mt-3 text-sm text-gray-500" data-test="fee-mode-final-hint">
                        {{ $t("projects.feeCalculationModeFinal") }}
                    </p>

                    <jet-input-error :message="form.errors['fee_calculation_mode']" class="mt-2" />
                </div>
            </form-card>            

            <!-- Card Project Contact -->
            <form-card :title="$t('projects.lead')" :description="$t('projects.leadDescription')">
                <div class="col-span-6">
                    <label for="company" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.company")
                        }} <RequiredMark /></label>
                    <input type="text" name="company" id="company" v-model="form.contact.company"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.company']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="firstname" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.firstname")
                        }} <RequiredMark /></label>
                    <input type="text" name="firstname" id="firstname" v-model="form.contact.firstname"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.firstname']" class="mt-2" />
                </div>

                <div class="col-span-6 sm:col-span-3 lg:col-span-3">
                    <label for="lastname" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.lastname")
                        }} <RequiredMark /></label>
                    <input type="text" name="lastname" id="lastname" v-model="form.contact.lastname"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.lastname']" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="email" class="block text-sm font-medium text-gray-700">{{ $t("contact.email") }} <RequiredMark /></label>
                    <input type="text" name="email" id="email" v-model="form.contact.email"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.email']" class="mt-2" />
                </div>


                <div class="col-span-6">
                    <label for="phone" class="block text-sm font-medium text-gray-700">{{
                            $t("contact.mobile")
                        }} <RequiredMark /></label>
                    <input type="text" name="phone" id="phone" v-model="form.contact.mobile"
                           :maxlength="$page.props.branding.mobile_number_max_digit || 12"
                           @input="form.contact.mobile = form.contact.mobile.replace(/[^0-9]/g, '').slice(0, $page.props.branding.mobile_number_max_digit || 12)"
                           class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300" />
                    <jet-input-error :message="form.errors['contact.mobile']" class="mt-2" />
                </div>
            </form-card>
        </template>

        <template #actions>
            <div class="flex justify-end mt-4">
                <primary-button :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                    {{ $t("actions.save") }}
                </primary-button>
            </div>
        </template>

    </jet-form-section>

</template>


<script>
import { Head } from "@inertiajs/inertia-vue3";
import { countries } from "@/Shared/countries";
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import { useForm } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import PageTitle from "@/Shared/Components/PageTitle";
import RequiredMark from "@/Shared/Components/RequiredMark";

export default defineComponent({
    // Bis zum 2026-08-20 lieferte `advisors.projects.create` gar keine Props.
    // `feeCalculation` traegt den Modus der Agentur als Vorbelegung und die
    // Liste der zulaessigen Werte (Entscheid 5).
    props: ["feeCalculation"],
    layout: MainLayout,
    components: {
        Head,
        PageTitle,
        FormCard,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        RequiredMark
    },
    data() {
        return {
            countries,
            imagePreview: null
        };
    },
    computed: {
        maxSizeInMb() {
            const bytes = this.$page.props.branding?.max_project_image_upload_size || (20 * 1024 * 1024);
            return Math.round(bytes / 1024 / 1024);
        }
    },
    methods: {
        createProject() {
            if (this.$refs.image && this.$refs.image.files[0]) {
                const fsize = this.$refs.image.files[0].size;
                const maxBytes = this.$page.props.branding?.max_project_image_upload_size || (20 * 1024 * 1024);

                if (fsize > maxBytes) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        text: "projects.fileTooBig"
                    });
                    return;
                }

                this.form.image = this.$refs.image.files[0];
            }

            this.form.post(this.route("advisors.projects.store"), {
                preserveScroll: true,
                onSuccess: () => this.$notify({
                    group: "success",
                    title: 'actions.success',
                    text: 'projects.created'
                })
            });
        },
        selectNewPhoto() {
            this.$refs.image.click();
        },
        updateImagePreview() {
            const image = this.$refs.image.files[0];

            if (!image) return;

            const fsize = image.size;
            const maxBytes = this.$page.props.branding?.max_project_image_upload_size || (20 * 1024 * 1024);

            if (fsize > maxBytes) {
                this.$notify({
                    group: "error",
                    title: "actions.error",
                    text: "projects.fileTooBig"
                });
                this.$refs.image.value = null;
                return;
            }

            const reader = new FileReader();

            reader.onload = (e) => {
                this.imagePreview = e.target.result;
            };

            reader.readAsDataURL(image);
        },

        clearPhotoFileInput() {
            if (this.$refs.image?.value) {
                this.$refs.image.value = null;
                this.imagePreview = null;
            }
        }
    },
    setup(props) {
        const form = useForm({
            name: null,
            number: null,
            image: null,
            location: {
                street: null,
                zip: null,
                city: null,
                country: null
            },
            contact: {
                company: null,
                firstname: null,
                lastname: null,
                mobile: null,
                email: null
            },
            base_fee: null,
            consultancy_fee: null,
            // Vorbelegt aus der Agentur. `setup()` sieht `this` nicht — die
            // Prop kommt darum ueber das erste Argument.
            fee_calculation_mode: props.feeCalculation?.mode ?? null,
        });

        return { form };
    }
});
</script>

<style scoped></style>
